import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle } from "lucide-react";

const reportSchema = z.object({
  category: z.enum(['technical', 'behavioral', 'financial', 'session', 'other']),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
  description: z.string().min(20, "Description must be at least 20 characters").max(2000, "Description must be under 2000 characters"),
});

type ReportFormData = z.infer<typeof reportSchema>;

interface ReportSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  mentorName: string;
  sessionDate: string;
  userRole: 'student' | 'mentor';
}

const categoryLabels = {
  technical: "Technical Issue",
  behavioral: "Behavioral Concern",
  financial: "Financial/Payment Issue",
  session: "Session Problem",
  other: "Other Issue"
};

export default function ReportSessionModal({
  isOpen,
  onClose,
  sessionId,
  mentorName,
  sessionDate,
  userRole,
}: ReportSessionModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<ReportFormData>({
    resolver: zodResolver(reportSchema),
    defaultValues: {
      category: 'session',
      priority: 'medium',
      description: "",
    },
  });

  const handleSubmit = async (data: ReportFormData) => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const title = `Session Report: ${mentorName} - ${sessionDate}`;
      const description = `**Session Report from ${userRole.charAt(0).toUpperCase() + userRole.slice(1)}**\n\n` +
        `**Session ID:** ${sessionId}\n` +
        `**Mentor:** ${mentorName}\n` +
        `**Session Date:** ${sessionDate}\n` +
        `**Category:** ${categoryLabels[data.category]}\n\n` +
        `**Description:**\n${data.description}`;

      const { error } = await supabase
        .from('support_tickets')
        .insert({
          user_id: user.id,
          title,
          description,
          category: data.category,
          priority: data.priority,
        });

      if (error) throw error;

      toast({
        title: "Report Submitted",
        description: "Your session report has been submitted to our support team.",
      });

      onClose();
      form.reset();
    } catch (error: any) {
      console.error('Error submitting report:', error);
      toast({
        title: "Error",
        description: "Failed to submit report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            Report Session Issue
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-muted/50 p-3 rounded-lg">
            <p className="text-sm"><strong>Session:</strong> {mentorName}</p>
            <p className="text-sm"><strong>Date:</strong> {sessionDate}</p>
          </div>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Issue Category</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(categoryLabels).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Describe the Issue</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Please describe what happened during the session that you'd like to report..."
                        className="resize-none"
                        rows={5}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Report"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}